// The app's small animations — the parts CSS can't do alone. Everything is
// opt-in through attributes, and nothing runs for people who ask for
// reduced motion (the CSS side collapses durations for them too).
//
//   data-motion-enter   on a list row: fades up when a Livewire update adds it
//   data-motion-exit    on a list row: fades and folds away when an update removes it
//   data-motion-confirm on a wire:click button: shows a tick once the action succeeds
//   x-count             on a number: counts up when first seen, and to new values
//   x-roll              on a number: rolls into place whenever it changes
//   x-motion-segmented  on a segmented control: the current option's fill slides
//
// Submit buttons of Livewire forms get the tick automatically, and fields
// that fail validation give one small shake.

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SNAPPY = 'cubic-bezier(0.2, 0, 0, 1)';

/* ------------------------------------------------------------------ */
/* Numbers                                                             */
/* ------------------------------------------------------------------ */

// "€1,234.50" → { prefix: '€', value: 1234.5, decimals: 2, grouping: true, suffix: '' }
function parseNumber(text) {
    const match = text.match(/^([\s\S]*?)(\d[\d,]*(?:\.\d+)?)([\s\S]*)$/);

    if (! match) {
        return null;
    }

    const [, prefix, digits, suffix] = match;

    return {
        prefix,
        suffix,
        value: parseFloat(digits.replace(/,/g, '')),
        decimals: (digits.split('.')[1] ?? '').length,
        grouping: digits.includes(','),
        text,
    };
}

function formatNumber(value, shape) {
    return shape.prefix + value.toLocaleString('en-US', {
        minimumFractionDigits: shape.decimals,
        maximumFractionDigits: shape.decimals,
        useGrouping: shape.grouping,
    }) + shape.suffix;
}

// Writes intermediate values into the element and always ends on the exact
// original text, so formatting the parser can't model still lands right.
function tweenNumber(el, from, to, duration = 550) {
    const start = performance.now();

    cancelAnimationFrame(el.__motionFrame);

    const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);

        el.__motionWritten = t < 1 ? formatNumber(from + (to.value - from) * eased, to) : to.text;
        el.textContent = el.__motionWritten;

        if (t < 1) {
            el.__motionFrame = requestAnimationFrame(step);
        }
    };

    el.__motionFrame = requestAnimationFrame(step);
}

function roll(el) {
    if (reducedMotion()) {
        return;
    }

    el.classList.remove('motion-roll');
    void el.offsetWidth;
    el.classList.add('motion-roll');
}

// Calls back whenever something other than our own tween rewrites the text.
function onTextChange(el, callback) {
    const observer = new MutationObserver(() => {
        if (el.textContent === el.__motionWritten) {
            return;
        }

        callback();
    });

    observer.observe(el, { childList: true, characterData: true, subtree: true });

    return () => observer.disconnect();
}

/* ------------------------------------------------------------------ */
/* Rows                                                                */
/* ------------------------------------------------------------------ */

function enter(el) {
    el.classList.add('motion-entered');
    el.addEventListener('animationend', () => el.classList.remove('motion-entered'), { once: true });
}

// A copy of a row that only exists to be animated away: no Livewire or
// Alpine wiring (so nothing re-initialises), no key, no ids, unclickable.
function inert(node) {
    [node, ...node.querySelectorAll('*')].forEach((child) => {
        [...child.attributes].forEach(({ name }) => {
            if (name.startsWith('wire:') || name.startsWith('x-') || name.startsWith('@') || name.startsWith(':') || name === 'id') {
                child.removeAttribute(name);
            }
        });
    });

    node.setAttribute('aria-hidden', 'true');
    node.inert = true;

    return node;
}

// Fade and slide a little, then fold the height so the rows below close up.
function exit(el) {
    const style = getComputedStyle(el);
    const height = `${el.offsetHeight}px`;

    el.__motionLeaving = true;
    el.style.overflow = 'hidden';
    el.style.pointerEvents = 'none';

    el.animate([
        { opacity: 1, transform: 'none', height, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom, borderTopWidth: style.borderTopWidth },
        { opacity: 0, transform: 'translateX(12px)', height, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom, borderTopWidth: style.borderTopWidth, offset: 0.45 },
        { opacity: 0, transform: 'translateX(12px)', height: '0px', paddingTop: '0px', paddingBottom: '0px', borderTopWidth: '0px' },
    ], { duration: 340, easing: SNAPPY, fill: 'forwards' }).finished.finally(() => el.remove());
}

/* ------------------------------------------------------------------ */
/* After an action: the saved tick, or a shake on invalid fields       */
/* ------------------------------------------------------------------ */

function tick(button) {
    clearTimeout(button.__motionTick);
    button.setAttribute('data-motion-done', '');
    button.__motionTick = setTimeout(() => button.removeAttribute('data-motion-done'), 1100);
}

function shake(el) {
    el.classList.remove('motion-shake');
    void el.offsetWidth;
    el.classList.add('motion-shake');
    el.addEventListener('animationend', () => el.classList.remove('motion-shake'), { once: true });
}

function afterAction(origin) {
    if (! origin.isConnected) {
        return;
    }

    const form = origin.closest('form');
    const isSubmit = form !== null && (origin === form || origin.matches('[type=submit]'));

    if (isSubmit) {
        const invalid = form.querySelector('[data-invalid], [aria-invalid="true"], [data-flux-error]:not(.hidden)');

        if (invalid) {
            shake(invalid.closest('[data-flux-field]') ?? invalid);

            return;
        }

        const button = origin === form ? form.querySelector('[type=submit][data-flux-button]') : origin;

        if (button?.isConnected && button.matches('[data-flux-button]')) {
            tick(button);
        }

        return;
    }

    const confirm = origin.closest('[data-motion-confirm]');

    if (confirm) {
        tick(confirm);
    }
}

/* ------------------------------------------------------------------ */
/* Segmented controls                                                  */
/* ------------------------------------------------------------------ */

function segmented(el) {
    const place = (option) => {
        // Livewire re-renders strip attributes the server didn't send, so
        // this re-applies the marker as well as the position every time.
        el.setAttribute('data-motion-segmented', '');

        option ??= el.querySelector(':scope > [data-current], :scope > [aria-checked="true"], :scope > [aria-pressed="true"], :scope > [data-checked]');

        if (! option) {
            el.removeAttribute('data-motion-ready');

            return;
        }

        el.style.setProperty('--seg-x', `${option.offsetLeft}px`);
        el.style.setProperty('--seg-y', `${option.offsetTop}px`);
        el.style.setProperty('--seg-w', `${option.offsetWidth}px`);
        el.style.setProperty('--seg-h', `${option.offsetHeight}px`);
        el.setAttribute('data-motion-ready', '');
    };

    // Move at once on click, before the server answers; the observer then
    // settles it on whatever the response marks as current.
    const onClick = (event) => {
        let option = event.target;

        while (option && option.parentElement !== el) {
            option = option.parentElement;
        }

        if (option) {
            place(option);
        }
    };

    const observer = new MutationObserver(() => place());
    const resize = new ResizeObserver(() => place());

    el.__motionPlace = place;
    observer.observe(el, { attributes: true, subtree: true, attributeFilter: ['data-current', 'aria-checked', 'aria-pressed', 'data-checked'] });
    resize.observe(el);
    el.addEventListener('click', onClick);
    place();

    return () => {
        observer.disconnect();
        resize.disconnect();
        el.removeEventListener('click', onClick);
    };
}

/* ------------------------------------------------------------------ */
/* Wiring                                                              */
/* ------------------------------------------------------------------ */

document.addEventListener('alpine:init', () => {
    const Alpine = window.Alpine;

    Alpine.directive('count', (el, directive, { cleanup }) => {
        let last = parseNumber(el.textContent.trim());

        // Count up the first time the number comes into view.
        if (last && last.value > 0 && ! reducedMotion()) {
            el.textContent = formatNumber(0, last);

            const seen = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    seen.disconnect();
                    tweenNumber(el, 0, last);
                }
            });

            seen.observe(el);
            cleanup(() => seen.disconnect());
        }

        cleanup(onTextChange(el, () => {
            const next = parseNumber(el.textContent.trim());

            if (next && last && next.value !== last.value && ! reducedMotion()) {
                tweenNumber(el, last.value, next);
            }

            last = next;
        }));
    });

    Alpine.directive('roll', (el, directive, { cleanup }) => {
        cleanup(onTextChange(el, () => roll(el)));
    });

    Alpine.directive('motion-segmented', (el, directive, { cleanup }) => {
        cleanup(segmented(el));
    });

    // Work anywhere, not only inside x-data (the layout, Blade components).
    Alpine.addInitSelector(() => '[x-count], [x-roll], [x-motion-segmented]');
});

document.addEventListener('livewire:init', () => {
    const Livewire = window.Livewire;

    // Leaving rows: morphing detaches a keyed row the moment its successor
    // takes its place, so there is no removal to delay. Instead, note which
    // rows won't survive the update, then put a lifeless copy back where
    // each one stood and let that copy fade and fold away.
    Livewire.hook('morph', ({ el, toEl }) => {
        if (reducedMotion()) {
            return;
        }

        const surviving = new Set([...toEl.querySelectorAll('[wire\\:key]')].map((node) => node.getAttribute('wire:key')));
        const survives = (node) => surviving.has(node.getAttribute('wire:key'));

        // Morphing keeps keyed survivors as the same nodes but may clone
        // unkeyed ones, so anchor on the nearest surviving keyed sibling
        // and fall back to the row's position.
        const nearest = (node, step) => {
            for (let sibling = node[step]; sibling; sibling = sibling[step]) {
                if (sibling.hasAttribute('wire:key') && survives(sibling)) {
                    return sibling;
                }
            }

            return null;
        };

        el.__motionGhosts = [...el.querySelectorAll('[data-motion-exit][wire\\:key]')]
            .filter((node) => ! survives(node))
            .map((node) => ({
                node,
                ghost: inert(node.cloneNode(true)),
                parent: node.parentElement,
                previous: node.previousElementSibling,
                after: nearest(node, 'previousElementSibling'),
                before: nearest(node, 'nextElementSibling'),
                index: [...node.parentElement.children].indexOf(node),
            }));
    });

    Livewire.hook('morphed', ({ el }) => {
        const leaving = el.__motionGhosts ?? [];
        const ghostOf = new Map(leaving.map(({ node, ghost }) => [node, ghost]));
        const inPlace = (node, parent) => node?.isConnected && node.parentElement === parent;

        el.__motionGhosts = null;

        leaving.forEach(({ ghost, parent, previous, after, before, index }) => {
            if (! parent.isConnected) {
                return; // a leaving ancestor already carries this row out
            }

            // Consecutive leavers stay in order behind each other's copies.
            const previousGhost = ghostOf.get(previous);

            if (inPlace(previousGhost, parent)) {
                previousGhost.after(ghost);
            } else if (inPlace(after, parent)) {
                after.after(ghost);
            } else if (inPlace(before, parent)) {
                parent.insertBefore(ghost, before);
            } else {
                parent.insertBefore(ghost, parent.children[index] ?? null);
            }

            exit(ghost);
        });
    });

    // A copy left behind by a later update is already on its way out.
    Livewire.hook('morph.removing', ({ el, skip }) => {
        if (el.__motionLeaving) {
            skip();
        }
    });

    // A re-render resets a segmented control's attributes; put the fill back.
    Livewire.hook('morph.updated', ({ el }) => {
        el.__motionPlace?.();
    });

    Livewire.hook('morph.added', ({ el }) => {
        if (el.matches?.('[data-motion-enter]') && ! reducedMotion()) {
            enter(el);
        }
    });

    Livewire.interceptMessage(({ message, onSend, onSuccess }) => {
        let origins = [];

        onSend(() => {
            origins = [...message.actions]
                .map((action) => action.origin?.targetEl ?? action.origin?.el)
                .filter(Boolean);
        });

        onSuccess(({ onMorphed }) => {
            onMorphed(() => {
                if (! reducedMotion()) {
                    origins.forEach(afterAction);
                }
            });
        });
    });
});

// The sidebar's width only eases while someone is toggling it, never as
// Flux restores the saved state on load.
document.addEventListener('pointerdown', (event) => {
    const sidebar = event.target.closest?.('[data-flux-sidebar]');

    if (! sidebar || sidebar.__motionWatching) {
        return;
    }

    sidebar.__motionWatching = true;

    const observer = new MutationObserver(() => {
        observer.disconnect();
        sidebar.setAttribute('data-motion-resizing', '');
        setTimeout(() => sidebar.removeAttribute('data-motion-resizing'), 360);
    });

    observer.observe(sidebar, { attributes: true, attributeFilter: ['data-flux-sidebar-collapsed-desktop'] });

    setTimeout(() => {
        observer.disconnect();
        sidebar.__motionWatching = false;
    }, 400);
}, true);

// Shared with inline Alpine snippets (countdowns, the gallery viewer).
window.bookifyMotion = { reducedMotion, roll, enter, exit, tick, shake, tweenNumber, parseNumber };
